import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }
function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }
import React, { useCallback, useMemo, useRef, useState } from 'react';
import rafSchd from 'raf-schd';
import { getGuidelinesWithHighlights } from '@atlaskit/editor-common/guideline';
import { ResizerNext } from '@atlaskit/editor-common/resizer';
import { resizerHandleShadowClassName } from '@atlaskit/editor-common/styles';
import { findTable } from '@atlaskit/editor-tables/utils';
import { getPluginState } from '../pm-plugins/plugin-factory';
import { COLUMN_MIN_WIDTH, getColgroupChildrenLength, previewScaleTable, scaleTable } from '../pm-plugins/table-resizing/utils';
import { pluginKey as tableWidthPluginKey } from '../pm-plugins/table-width';
import { TABLE_HIGHLIGHT_GAP, TABLE_HIGHLIGHT_TOLERANCE, TABLE_SNAP_GAP } from '../ui/consts';
import { generateResizedPayload, generateResizeFrameRatePayloads, useMeasureFramerate } from '../utils/analytics';
import { defaultGuidelines } from '../utils/guidelines';
import { defaultSnappingWidths, findClosestSnap } from '../utils/snapping';
var handles = {
  right: true
};
var tableHandleMarginTop = 12;
var tableHandlePosition = 14;
var getResizerHandleHeight = function getResizerHandleHeight(tableRef) {
  var tableHeight = tableRef === null || tableRef === void 0 ? void 0 : tableRef.clientHeight;
  var handleHeightSize = 'small';
  if (!tableHeight) {
    return handleHeightSize;
  }

  /*
    - One row table height (minimum possible table height) ~ 45px
    - Two row table height ~ 90px
    - Three row table height ~ 134px
     In the if below we need to use:
    - > 46 because the height of the table can be a float number like 45.44.
    - < 96 is the height of large resize handle.
  */
  if (tableHeight > 46 && tableHeight < 96) {
    handleHeightSize = 'medium';
  } else if (tableHeight >= 96) {
    handleHeightSize = 'large';
  }
  return handleHeightSize;
};
var getResizerMinWidth = function getResizerMinWidth(node) {
  var currentColumnCount = getColgroupChildrenLength(node);
  var minColumnWidth = currentColumnCount <= 3 ? currentColumnCount * COLUMN_MIN_WIDTH : 3 * COLUMN_MIN_WIDTH;
  // add an extra pixel as the scale table logic will scale columns to be tableContainerWidth - 1
  // the table can't scale past its min-width, so instead restrict table container min width to avoid that situation
  return minColumnWidth + 1;
};

/**
 * When guidelines are outside the viewport, filter them out, do not show
 * So the guideline container won't make the fabric-editor-popup-scroll-parent overflow
 * @param guidelines
 * @param containerWidth editorWidth
 */
var getVisibleGuidelines = function getVisibleGuidelines(guidelines, containerWidth) {
  return guidelines.filter(function (guideline) {
    return guideline.position && guideline.position.x !== undefined && typeof guideline.position.x === 'number' && Math.abs(guideline.position.x * 2) < containerWidth;
  });
};
export var TableResizer = function TableResizer(_ref) {
  var _findTable, _editorView$state;
  var children = _ref.children,
    width = _ref.width,
    maxWidth = _ref.maxWidth,
    containerWidth = _ref.containerWidth,
    updateWidth = _ref.updateWidth,
    editorView = _ref.editorView,
    getPos = _ref.getPos,
    node = _ref.node,
    tableRef = _ref.tableRef,
    displayGuideline = _ref.displayGuideline,
    attachAnalyticsEvent = _ref.attachAnalyticsEvent;
  var currentGap = useRef(0);
  var _useState = useState(false),
    _useState2 = _slicedToArray(_useState, 2),
    snappingEnabled = _useState2[0],
    setSnappingEnabled = _useState2[1];
  var resizerMinWidth = getResizerMinWidth(node);
  var handleHeightSize = getResizerHandleHeight(tableRef);
  var _getPluginState = getPluginState(editorView.state),
    isInDanger = _getPluginState.isInDanger;
  var _useMeasureFramerate = useMeasureFramerate(),
    startMeasure = _useMeasureFramerate.startMeasure,
    endMeasure = _useMeasureFramerate.endMeasure,
    countFrames = _useMeasureFramerate.countFrames;
  var updateActiveGuidelines = useCallback(function (_ref2) {
    var gap = _ref2.gap,
      keys = _ref2.keys;
    if (gap !== currentGap.current) {
      currentGap.current = gap;
      var visibleGuidelines = getVisibleGuidelines(defaultGuidelines, containerWidth);
      displayGuideline(getGuidelinesWithHighlights(gap, TABLE_SNAP_GAP, keys, visibleGuidelines));
    }
  }, [displayGuideline, containerWidth]);
  var guidelineSnaps = useMemo(function () {
    return snappingEnabled ? {
      x: defaultSnappingWidths
    } : undefined;
  }, [snappingEnabled]);
  var handleResizeStart = useCallback(function () {
    startMeasure();
    var dispatch = editorView.dispatch,
      tr = editorView.state.tr;
    dispatch(tr.setMeta(tableWidthPluginKey, {
      resizing: true
    }));
    var visibleGuidelines = getVisibleGuidelines(defaultGuidelines, containerWidth);
    setSnappingEnabled(displayGuideline(visibleGuidelines));
  }, [displayGuideline, editorView, startMeasure, containerWidth]);
  var handleResize = useCallback(function (originalState, delta) {
    countFrames();
    var newWidth = originalState.width + delta.width;
    var pos = getPos();
    if (typeof pos !== 'number') {
      return;
    }
    previewScaleTable(tableRef, {
      node: node,
      prevNode: node,
      start: pos + 1,
      parentWidth: newWidth
    }, editorView.domAtPos.bind(editorView));
    updateActiveGuidelines(findClosestSnap(newWidth, defaultSnappingWidths, defaultGuidelines, TABLE_HIGHLIGHT_GAP, TABLE_HIGHLIGHT_TOLERANCE));
    updateWidth(newWidth);
    return newWidth;
  }, [editorView, getPos, node, tableRef, updateWidth, updateActiveGuidelines, countFrames]);
  var scheduleResize = useMemo(function () {
    return rafSchd(handleResize);
  }, [handleResize]);
  var handleResizeStop = useCallback(function (originalState, delta) {
    var newWidth = originalState.width + delta.width;
    var state = editorView.state,
      dispatch = editorView.dispatch;
    var pos = getPos();
    var tr = state.tr.setMeta(tableWidthPluginKey, {
      resizing: false
    });
    var frameRateSamples = endMeasure();
    if (frameRateSamples.length > 0) {
      var resizeFrameRatePayloads = generateResizeFrameRatePayloads({
        docSize: state.doc.nodeSize,
        frameRateSamples: frameRateSamples,
        originalNode: node
      });
      resizeFrameRatePayloads.forEach(function (payload) {
        var _attachAnalyticsEvent;
        (_attachAnalyticsEvent = attachAnalyticsEvent(payload)) === null || _attachAnalyticsEvent === void 0 ? void 0 : _attachAnalyticsEvent(tr);
      });
    }
    if (typeof pos === 'number') {
      var _attachAnalyticsEvent2;
      tr = tr.setNodeMarkup(pos, undefined, _objectSpread(_objectSpread({}, node.attrs), {}, {
        width: newWidth
      }));
      var newNode = tr.doc.nodeAt(pos);
      tr = scaleTable(tableRef, {
        node: newNode,
        prevNode: node,
        start: pos + 1,
        parentWidth: newWidth
      }, editorView.domAtPos.bind(editorView))(tr);
      var scaledNode = tr.doc.nodeAt(pos);
      (_attachAnalyticsEvent2 = attachAnalyticsEvent(generateResizedPayload({
        originalNode: node,
        resizedNode: scaledNode
      }))) === null || _attachAnalyticsEvent2 === void 0 ? void 0 : _attachAnalyticsEvent2(tr);
    }
    dispatch(tr);

    // Hide guidelines when resizing stops
    displayGuideline([]);
    updateWidth(newWidth);
    scheduleResize.cancel();
    return newWidth;
  }, [updateWidth, editorView, getPos, node, tableRef, scheduleResize, displayGuideline, attachAnalyticsEvent, endMeasure]);
  var handleComponent = useMemo(function () {
    return {
      left: /*#__PURE__*/React.createElement("div", {
        className: resizerHandleShadowClassName,
        style: {
          height: 'calc(100% - 24px)'
        }
      }),
      right: /*#__PURE__*/React.createElement("div", {
        className: resizerHandleShadowClassName,
        style: {
          height: 'calc(100% - 24px)'
        }
      })
    };
  }, []);
  return /*#__PURE__*/React.createElement(ResizerNext, {
    enable: handles,
    width: width,
    handleAlignmentMethod: "sticky",
    handleHeightSize: handleHeightSize,
    handleMarginTop: tableHandleMarginTop,
    handleResizeStart: handleResizeStart,
    handleResize: scheduleResize,
    handleResizeStop: handleResizeStop,
    resizeRatio: 2,
    minWidth: resizerMinWidth,
    maxWidth: maxWidth,
    snapGap: TABLE_SNAP_GAP,
    snap: guidelineSnaps,
    handlePositioning: "adjacent",
    innerPadding: tableHandlePosition,
    isHandleVisible: ((_findTable = findTable((_editorView$state = editorView.state) === null || _editorView$state === void 0 ? void 0 : _editorView$state.selection)) === null || _findTable === void 0 ? void 0 : _findTable.pos) === getPos(),
    handleComponent: handleComponent,
    appearance: isInDanger ? 'danger' : undefined
  }, children);
};